import { useState, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { X, Loader2, Sparkles, Package, AlertCircle, CheckCircle, Hash } from 'lucide-react'
import { productAI, products } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import SearchableSelect from '@/components/SearchableSelect'

interface SelectedProduct {
  id: number
  name: string
  supplier_reference: string
  cost_price: number
}

interface BatchCreateModalProps {
  isOpen: boolean
  onClose: () => void
  selectedProducts: SelectedProduct[]
  supplierCode?: string
}

export default function BatchCreateModal({
  isOpen,
  onClose,
  selectedProducts,
  supplierCode
}: BatchCreateModalProps) {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [referencePrefix, setReferencePrefix] = useState('')
  const [referenceLoading, setReferenceLoading] = useState(false)
  const [referenceInfo, setReferenceInfo] = useState<{
    nextReference: string
    highest: string | null
    count: number
    prefix: string  // Prefijo para enviar al backend
  } | null>(null)
  const [warrantyDuration, setWarrantyDuration] = useState('3 años')
  const [aiModel, setAiModel] = useState('gemini-2.5-pro')
  const [autoApprove, setAutoApprove] = useState(true)
  const [createInFactusol, setCreateInFactusol] = useState(false)
  const [selectedFamily, setSelectedFamily] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState(supplierCode || '')

  // Result state
  const [result, setResult] = useState<{
    success: boolean
    jobs_created: number
    jobs_skipped: number
    jobs: Array<{ job_id: number; reference: string; name: string }>
    skipped: Array<{ supplier_product_id: number; name?: string; reason: string }>
    message: string
  } | null>(null)

  // Fetch PrestaShop categories
  const { data: categoriesData } = useQuery({
    queryKey: ['prestashop-categories'],
    queryFn: () => products.listCategoriesFromDB(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  // Fetch Factusol data
  const { data: familiesData } = useQuery({
    queryKey: ['factusol-families'],
    queryFn: () => productAI.getFactusolFamilies(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: createInFactusol,
  })

  const { data: suppliersData } = useQuery({
    queryKey: ['factusol-suppliers'],
    queryFn: () => productAI.getFactusolSuppliers(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: createInFactusol,
  })

  const prestashopCategories = categoriesData?.categories || []
  const factusolFamilies = familiesData?.families || []
  const factusolSuppliers = suppliersData?.suppliers || []

  // Category options for SearchableSelect
  const categoryOptions = useMemo(() => {
    return prestashopCategories.map((cat: any) => ({
      value: String(cat.id),
      label: `${cat.id} - ${cat.name}`
    }))
  }, [prestashopCategories])

  // Family options for SearchableSelect
  const familyOptions = useMemo(() => {
    return factusolFamilies.map((fam: any) => ({
      value: fam.CODFAM,
      label: `${fam.CODFAM} - ${fam.DESFAM}`
    }))
  }, [factusolFamilies])

  // Supplier options for SearchableSelect
  const supplierOptions = useMemo(() => {
    return factusolSuppliers.map((sup: any) => ({
      value: String(sup.CODPRO),
      label: `${sup.CODPRO} - ${sup.NOFPRO}`
    }))
  }, [factusolSuppliers])

  // Fetch next sequential reference
  const fetchNextSequentialReference = async () => {
    if (!referencePrefix || referencePrefix.length < 1) {
      return
    }

    // Añadir "-" si no lo tiene
    const prefix = referencePrefix.endsWith('-') ? referencePrefix : referencePrefix + '-'

    setReferenceLoading(true)
    setReferenceInfo(null)

    try {
      const result = await productAI.getNextSequentialReference(prefix)

      // Guardar info con la siguiente referencia y el prefijo usado
      setReferenceInfo({
        nextReference: result.next_reference,
        highest: result.highest_found,
        count: result.count,
        prefix: prefix  // Guardar el prefijo para enviarlo al backend
      })

      // Autocompletar el campo con la primera referencia (como en el formulario estándar)
      setReferencePrefix(result.next_reference)
    } catch (error) {
      console.error('Error obteniendo siguiente referencia:', error)
      toast.error('Error al buscar siguiente referencia')
    } finally {
      setReferenceLoading(false)
    }
  }

  // Batch create mutation
  const batchCreateMutation = useMutation({
    mutationFn: () => productAI.addBatchToQueue({
      supplier_product_ids: selectedProducts.map(p => p.id),
      category_id: parseInt(categoryId),
      reference_prefix: referenceInfo?.prefix || referencePrefix,  // Usar el prefijo guardado
      warranty_duration: warrantyDuration,
      ai_model: aiModel,
      auto_approve: autoApprove,
      factusol_create: createInFactusol,
      factusol_family: createInFactusol ? selectedFamily : undefined,
      factusol_supplier: createInFactusol ? selectedSupplier : undefined,
    }),
    onSuccess: (data) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ['product-queue'] })

      if (data.jobs_created > 0) {
        toast.success(`${data.jobs_created} productos añadidos a la cola`)
      }
      if (data.jobs_skipped > 0) {
        toast.warning(`${data.jobs_skipped} productos omitidos`)
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message || 'Error al crear lote'
      toast.error(message)
    }
  })

  // Form validation
  const isFormValid = useMemo(() => {
    if (!categoryId) return false
    if (!referencePrefix.trim()) return false
    if (!referenceInfo) return false  // Debe haber buscado la siguiente referencia
    if (createInFactusol && (!selectedFamily || !selectedSupplier)) return false
    return true
  }, [categoryId, referencePrefix, referenceInfo, createInFactusol, selectedFamily, selectedSupplier])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return
    batchCreateMutation.mutate()
  }

  const handleClose = () => {
    if (!batchCreateMutation.isPending) {
      setResult(null)
      setReferenceInfo(null)
      setReferencePrefix('')
      setCategoryId('')
      onClose()
    }
  }

  const goToQueue = () => {
    handleClose()
    navigate('/products/queue')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-amber-50 dark:bg-amber-900/30 px-6 py-4 border-b border-amber-100 dark:border-amber-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Crear Productos en Lote con IA
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedProducts.length} productos seleccionados
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={batchCreateMutation.isPending}
              className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700 rounded disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {result ? (
            // Result view
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                result.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium text-slate-900 dark:text-white">
                    {result.message}
                  </span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <p>Trabajos creados: <span className="font-medium text-green-600">{result.jobs_created}</span></p>
                  <p>Productos omitidos: <span className="font-medium text-yellow-600">{result.jobs_skipped}</span></p>
                </div>
              </div>

              {result.jobs.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                    Trabajos Creados:
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
                    {result.jobs.map((job) => (
                      <div key={job.job_id} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700 rounded">
                        <span className="font-mono text-xs bg-green-100 dark:bg-green-900 px-1.5 py-0.5 rounded">
                          {job.reference}
                        </span>
                        <span className="text-slate-600 dark:text-slate-400 truncate">
                          {job.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.skipped.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-2">
                    Productos Omitidos:
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
                    {result.skipped.map((skip, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <span className="text-slate-900 dark:text-white truncate">
                          {skip.name || `ID: ${skip.supplier_product_id}`}
                        </span>
                        <span className="text-xs text-yellow-600 dark:text-yellow-400">
                          ({skip.reason})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Form view
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selected products summary */}
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                  Productos a crear:
                </h4>
                <div className="max-h-32 overflow-y-auto space-y-1 text-sm">
                  {selectedProducts.map((product) => (
                    <div key={product.id} className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400 truncate flex-1">
                        {product.name}
                      </span>
                      <span className="font-mono text-xs text-slate-500 ml-2">
                        {product.supplier_reference}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Categoría PrestaShop *
                </label>
                <SearchableSelect
                  options={categoryOptions}
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder="Seleccionar categoría..."
                />
              </div>

              {/* Reference prefix */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Prefijo de Referencia *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referencePrefix}
                    onChange={(e) => {
                      setReferencePrefix(e.target.value.toUpperCase())
                      setReferenceInfo(null) // Limpiar info al escribir
                    }}
                    placeholder="Ej: PANT-INT"
                    className="input flex-1"
                  />
                  <button
                    type="button"
                    onClick={fetchNextSequentialReference}
                    disabled={!referencePrefix || referencePrefix.length < 1 || referenceLoading}
                    title="Buscar siguiente número correlativo en Factusol"
                    className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    {referenceLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Hash className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {referenceInfo ? (
                  <div className="text-xs mt-1 space-y-1">
                    <p className="text-slate-500 dark:text-slate-400">
                      {selectedProducts.length} productos: <span className="font-mono">{referenceInfo.nextReference}</span> → <span className="font-mono">{
                        (() => {
                          // Calcular la última referencia usando el prefix guardado
                          const prefix = referenceInfo.prefix
                          const numPart = referenceInfo.nextReference.slice(prefix.length)
                          const startNum = parseInt(numPart, 10)
                          const endNum = startNum + selectedProducts.length - 1
                          return `${prefix}${String(endNum).padStart(numPart.length, '0')}`
                        })()
                      }</span>
                    </p>
                    {referenceInfo.count > 0 && (
                      <p className="text-slate-400">
                        Última existente: <span className="font-mono">{referenceInfo.highest}</span> ({referenceInfo.count} productos)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Escribe el prefijo y pulsa <Hash className="inline h-3 w-3" /> para buscar la siguiente referencia disponible
                  </p>
                )}
              </div>

              {/* Warranty duration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Duración de Garantía
                </label>
                <select
                  value={warrantyDuration}
                  onChange={(e) => setWarrantyDuration(e.target.value)}
                  className="input"
                >
                  <option value="1 año">1 año</option>
                  <option value="2 años">2 años</option>
                  <option value="3 años">3 años</option>
                  <option value="5 años">5 años</option>
                </select>
              </div>

              {/* AI Model */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Modelo de IA
                </label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="input"
                >
                  <option value="gemini-2.5-pro">Gemini 2.5 Pro (Recomendado)</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash (Rápido)</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                </select>
              </div>

              {/* Auto approve */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autoApprove"
                  checked={autoApprove}
                  onChange={(e) => setAutoApprove(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="autoApprove" className="text-sm text-slate-700 dark:text-slate-300">
                  Aprobar y publicar automáticamente
                </label>
              </div>

              {/* Factusol section */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="createInFactusol"
                    checked={createInFactusol}
                    onChange={(e) => setCreateInFactusol(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="createInFactusol" className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                    <Package className="w-4 h-4" />
                    Crear también en Factusol
                  </label>
                </div>

                {createInFactusol && (
                  <div className="space-y-3 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Familia Factusol *
                      </label>
                      <SearchableSelect
                        options={familyOptions}
                        value={selectedFamily}
                        onChange={setSelectedFamily}
                        placeholder="Seleccionar familia..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Proveedor Factusol *
                      </label>
                      <SearchableSelect
                        options={supplierOptions}
                        value={selectedSupplier}
                        onChange={setSelectedSupplier}
                        placeholder="Seleccionar proveedor..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          {result ? (
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="btn btn-secondary flex-1"
              >
                Cerrar
              </button>
              <button
                onClick={goToQueue}
                className="btn btn-primary flex-1"
              >
                Ver Cola de Trabajo
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={batchCreateMutation.isPending}
                className="btn btn-secondary flex-1"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || batchCreateMutation.isPending}
                className="btn btn-primary flex-1"
              >
                {batchCreateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Añadir a Cola ({selectedProducts.length})
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
